'use client';

import React, { useState } from 'react';
import { KeyRound, Mail, ArrowLeft, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import Link from 'next/link';

type Step = 'email' | 'sent' | 'error';

export default function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('Inserisci il tuo indirizzo email'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setError('Indirizzo email non valido'); return; }

    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error ?? 'Errore invio email');
      }
      setStep('sent');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Errore invio email');
      setStep('error');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <KeyRound size={28} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Password Dimenticata</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {step === 'email' && 'Inserisci la tua email per ricevere il link di reset'}
            {step === 'sent' && 'Controlla la tua casella email'}
            {step === 'error' && 'Si è verificato un errore'}
          </p>
        </div>

        <div className="bg-white border border-border rounded-2xl p-6 shadow-sm">
          {step === 'email' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-foreground mb-1.5">Indirizzo Email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    className={`w-full pl-9 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${error ? 'border-red-300' : 'border-border'}`}
                    placeholder="tuo@email.it"
                    aria-required="true"
                    aria-invalid={error ? 'true' : undefined}
                    aria-describedby={error ? 'reset-email-error' : undefined}
                    autoFocus
                  />
                </div>
                {error && (
                  <p id="reset-email-error" role="alert" className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle size={11} /> {error}
                  </p>
                )}
              </div>

              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                {loading ? 'Invio in corso...' : 'Invia Link di Reset'}
              </button>
            </form>
          )}

          {step === 'sent' && (
            <div className="text-center py-4">
              <CheckCircle size={40} className="text-green-500 mx-auto mb-4" />
              <h2 className="font-semibold text-foreground mb-2">Email Inviata!</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Abbiamo inviato un link di reset a <strong>{email}</strong>. Controlla anche la cartella spam.
              </p>
              <p className="text-xs text-muted-foreground">Il link scade tra 1 ora.</p>
              <button onClick={() => setStep('email')} className="mt-4 text-xs text-primary hover:underline">
                Non hai ricevuto l'email? Riprova
              </button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-4">
              <AlertTriangle size={40} className="text-red-500 mx-auto mb-4" />
              <h2 className="font-semibold text-foreground mb-2">Errore</h2>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <button onClick={() => { setStep('email'); setError(''); }}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
                Riprova
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <Link href="/login" className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={14} /> Torna al Login
          </Link>
        </div>
      </div>
    </div>
  );
}
